<template>
    <div class="spotlight-widget spotlight-exchange-widget" :class="{ 'spotlight-widget--clickable': !error && !loading }" @click="copyResult">
        <template v-if="loading">
            <div class="spotlight-widget-icon spotlight-widget-icon--exchange">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
            </div>
            <div class="spotlight-widget-content">
                <div class="spotlight-widget-result spotlight-widget-result--loading">...</div>
            </div>
        </template>
        <template v-else-if="error">
            <div class="spotlight-widget-icon spotlight-widget-icon--error">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            </div>
            <div class="spotlight-widget-content">
                <div class="spotlight-widget-error-text">{{ error }}</div>
            </div>
        </template>
        <template v-else>
            <div class="spotlight-widget-icon spotlight-widget-icon--exchange">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
            </div>
            <div class="spotlight-widget-content">
                <div class="spotlight-widget-result"><span class="spotlight-widget-eq">=</span> {{ formattedResult }}</div>
                <div class="spotlight-widget-expression">{{ query.expression }} <span class="spotlight-widget-arrow">→</span> {{ targetCurrency }}</div>
            </div>
            <div v-if="copied" class="spotlight-widget-copied">{{ $t('common.spotlight.copied') }}</div>
        </template>
    </div>
</template>

<script>
import { CURRENCY_SYMBOLS, fetchRates, formatCurrency } from './currencyRates'

export default {
    name: 'CurrencyMathCalculator',
    props: {
        query: { type: Object, required: true }
    },
    data() {
        return {
            rates: null,
            loading: false,
            error: null,
            copied: false
        }
    },
    computed: {
        defaultCurrency() {
            return this.$root.defaultCurrency?.code || 'TRY'
        },
        targetCurrency() {
            if (this.query.target) return this.query.target
            if (this.query.currencies.length === 1) return this.query.currencies[0]
            return this.defaultCurrency
        },
        result() {
            if (!this.rates) return null
            const target = this.targetCurrency
            const targetRate = this.rates[target]
            if (!targetRate) return null

            const tokens = this.query.tokens
            const singleCurrency = this.query.currencies.length === 1 && !this.query.target

            const resolved = []
            for (let i = 0; i < tokens.length; i++) {
                const t = tokens[i]
                if (t.type !== 'percent') { resolved.push(t); continue }

                const prev = resolved.length ? resolved[resolved.length - 1] : null
                if (prev && prev.type === 'op') {
                    if (prev.op === '+') {
                        // a + %18 → a * 1.18
                        resolved.pop()
                        resolved.push({ type: 'op', op: '*' })
                        resolved.push({ type: 'number', value: 1 + t.value / 100 })
                    } else if (prev.op === '-') {
                        // a - %20 → a * 0.80
                        resolved.pop()
                        resolved.push({ type: 'op', op: '*' })
                        resolved.push({ type: 'number', value: 1 - t.value / 100 })
                    } else {
                        // * veya / → düz oran
                        resolved.push({ type: 'number', value: t.value / 100 })
                    }
                } else {
                    resolved.push({ type: 'number', value: t.value / 100 })
                }
            }

            let parts = []
            for (const t of resolved) {
                if (t.type === 'op') {
                    parts.push(t.op)
                } else if (t.type === 'number') {
                    parts.push(String(t.value))
                } else if (t.type === 'amount') {
                    const fromRate = this.rates[t.currency]
                    if (!fromRate) return null
                    if (singleCurrency) {
                        parts.push(String(t.value))
                    } else {
                        const converted = t.value * (targetRate / fromRate)
                        parts.push(String(converted))
                    }
                }
            }

            try {
                const expr = parts.join(' ')
                const val = new Function('return (' + expr + ')')()
                if (typeof val !== 'number' || !isFinite(val)) return null
                return val
            } catch (e) {
                return null
            }
        },
        formattedResult() {
            if (this.result === null) return '...'
            const sym = CURRENCY_SYMBOLS[this.targetCurrency] || this.targetCurrency
            return formatCurrency(this.result) + ' ' + sym
        }
    },
    watch: {
        'query.expression': { handler: 'loadRates', immediate: true }
    },
    methods: {
        async loadRates() {
            this.loading = true
            this.error = null
            try {
                this.rates = await fetchRates(this.axios, this.endpoints.currency)
            } catch (e) {
                this.error = this.$t('common.spotlight.currencyError')
            } finally {
                this.loading = false
            }
        },
        async copyResult() {
            if (this.error || this.loading || this.result === null) return
            try {
                await navigator.clipboard.writeText(String(this.result.toFixed(2)))
                this.copied = true
                setTimeout(() => { this.copied = false }, 1500)
            } catch (e) { void e }
        }
    }
}
</script>
